import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2, Package } from "lucide-react";
import { ORDER_STATUS_BADGE } from "../dealer/DealerOrders";
import { ORDER_STATUS, human } from "@/lib/statusLabels";

const FILTERS = [
  { key: "", label: "All" },
  { key: "ADMIN_REVIEW", label: "Needs review" },
  { key: "CHECKOUT_HOLD", label: "Unpaid holds" },
  { key: "PAYMENT_PROCESSING", label: "ACH processing" },
  { key: "PAID", label: "Paid" },
  { key: "PARTIALLY_FULFILLED", label: "Partial" },
  { key: "FULFILLED", label: "Fulfilled" },
];

export default function AdminOrders() {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    api
      .get(`/admin/orders${filter ? `?status=${filter}` : ""}`)
      .then((res) => setRows(res.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, [filter]);

  return (
    <div data-testid="admin-orders-page">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">Sales</p>
      <h1 className="mt-3 font-display text-4xl font-light tracking-tight text-charcoal md:text-5xl">Orders</h1>

      <div className="mt-6 flex flex-wrap gap-2" data-testid="order-filters">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            data-testid={`filter-${f.key || "all"}`}
            className={`rounded-full border px-4 py-1.5 text-xs font-medium transition-colors ${filter === f.key ? "border-terracotta bg-terracotta text-white" : "border-border bg-white text-warmgrey hover:text-charcoal"}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {!rows ? (
        <Loader2 className="mt-12 h-6 w-6 animate-spin text-terracotta" />
      ) : rows.length === 0 ? (
        <Card className="mt-8 border-border bg-white">
          <CardContent className="flex flex-col items-center p-16 text-center">
            <Package className="h-8 w-8 text-muted-foreground" />
            <p className="mt-4 text-sm text-muted-foreground" data-testid="admin-orders-empty">No orders in this view.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="mt-8 space-y-3">
          {rows.map((o) => (
            <Card key={o.id} className="cursor-pointer border-border bg-white transition-all duration-200 hover:-translate-y-[1px] hover:shadow-sm" data-testid={`admin-order-${o.id}`} onClick={() => navigate(`/admin/orders/${o.id}`)}>
              <CardContent className="flex flex-wrap items-center justify-between gap-4 p-5">
                <div>
                  <p className="font-medium text-charcoal">
                    {o.order_number} · {o.dealer_name}
                    {o.is_test_data && <span className="badge-status badge-reserved ml-2" data-testid={`order-test-badge-${o.order_number}`}>TEST</span>}
                  </p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {o.lines.map((l) => `${l.variant_label} ×${l.boxes}`).join(" · ")}
                    {" · "}{new Date(o.created_at).toLocaleString()}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {o.payment_reversal && <span className="badge-status badge-sold" data-testid={`reversal-flag-${o.id}`}>reversal</span>}
                  <span className="text-sm font-medium text-charcoal">${o.totals.total_usd.toFixed(2)}</span>
                  <span className={`badge-status ${ORDER_STATUS_BADGE[o.status]}`} data-testid={`admin-order-status-${o.id}`}>{human(ORDER_STATUS, o.status)}</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
